/* actions for loading the necessary data from the database into the global state */
import { menuItems } from '../pages/recursive_menu/menuItems';
import { initialState } from './DatabaseStore';
import {
  deriveChecklistTemplateCollections,
  reconstructChecklistTemplateUniverse,
  removeChecklistTemplateRowsByIds,
  resetChecklistTemplatesLookup,
  upsertCanonicalChecklistTemplateRow,
} from '../utils/checklistTemplatesLookup';

export function DatabaseReducer(state, action) {
    const hiddenTabs = ["SupportTicketAdd", "SupportTicketList", "SupportTicketDetail",];
    switch (action.type) {
      case 'load_departments': {
        return {
          ...state,
          departments: action.newDepartments,
        };
      }
      case 'load_employees': {
        return {
          ...state,
          employees: action.newEmployees
        };
      }
      case 'load_customers': {
        return {
          ...state,
          customers: action.newCustomers
        };
      }
      case 'load_companies': {
        return {
          ...state,
          companies: action.newCompanies,
          calibration_companies: action.newCompanies.filter(company => company.types.includes("calibration_company")),
          // training_companies – na progressive loading, neukladať zo init; dropdowny načítavajú z API
          training_companies: [],
          maintenance_companies: action.newCompanies.filter(company => company.types.includes("service_company")),
          otk_companies: action.newCompanies.filter(company => company.types.includes("customer") || company.types.includes("supplier")),
          customers: action.newCompanies.filter(company => company.types.includes("customer"))
        };
      }
      case 'load_company_statistics': {
        return {
          ...state,
          company_statistics: action.newCompanyStatistics
        };
      }
      // load_company_certificates – vypnuté, certifikáty sa načítavajú cez paged API v listoch/detaloch
      case 'load_company_contacts': {
        const list = action.newCompanyContacts ?? [];
        return {
          ...state,
          company_contacts: list,
          maintenance_company_contacts: list.filter(contact => contact?.module === 'maintenance') ?? state.maintenance_company_contacts,
        };
      }
      case 'load_error_types': {
        return {
          ...state,
          error_types: action.newErrorTypes
        };
      }
      case 'load_protocol_types': {
        return {
          ...state,
          protocol_types: action.newProtocolTypes
        };
      }
      case 'load_root_causes': {
        return {
          ...state,
          root_causes: action.newRootCauses
        };
      }
      case 'load_groups': {
        return {
          ...state,
          groups: action.newGroups
        };
      }
      case 'load_suppliers': {
        return {
          ...state,
          suppliers: action.newSuppliers
        };
      }
      case 'load_preventions': {
        return {
          ...state,
          preventions: action.newPreventions
        };
      }
      case 'load_summary_emails': {
        return {
          ...state,
          summary_emails_otk: action.newSummaryEmails.filter(email => email.type == 'otk'),
          summary_emails_painting: action.newSummaryEmails.filter(email => email.type == 'painting'),
          summary_emails_calibration: action.newSummaryEmails.filter(email => email.type == 'calibration'), 
          summary_emails_training: action.newSummaryEmails.filter(email => email.type == 'training'),
          summary_emails_checklists: action.newSummaryEmails.filter(email => email.type == 'checklists'), 
          summary_emails_maintenance: action.newSummaryEmails.filter(email => email.type == 'maintenance'),
        };
      }
      case 'load_checklist_holidays': {
        return {
          ...state,
          checklist_holidays: action.newChecklistHolidays || [],
        };
      }
      case 'load_company_settings_print': {
        return {
          ...state,
          company_settings_print: action.newCompanySettingsPrint ?? null,
        };
      }
      case 'load_branding': {
        return {
          ...state,
          branding: action.newBranding ?? null,
        };
      }
      case 'load_module_settings': {
        // Normalizujeme value na string (0/1 → '0'/'1'), aby SelectButton a getSetting vždy mali konzistentný tvar
        const normalized = (action.newModuleSettings || []).map(s => {
          const v = s.value;
          const str = (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : (v == null ? '' : String(v));
          return { ...s, value: str };
        });
        return {
          ...state,
          module_settings: normalized,
        };
      }
      case 'update_module_setting': {
        const val = action.value;
        const normalized = (val === 0 || val === '0') ? '0' : (val === 1 || val === '1') ? '1' : (val == null ? '' : String(val));
        const next = (state.module_settings || []).map(s =>
          s.id === action.id ? { ...s, value: normalized } : s
        );
        return { ...state, module_settings: next };
      }
      case 'load_lecturers': {
        return {
          ...state,
          lecturers: action.newLecturers
        };
      }
      case 'load_offenders': {
        return {
          ...state,
          offenders: action.newOffenders
        };
      }
      case 'load_error_records': {
        return {
          ...state,
          error_records: action.newErrorRecords,
          error_records_int: action.newErrorRecords.filter(error_record => error_record.type == 'Interná chyba'),
          error_records_ext: action.newErrorRecords.filter(error_record => error_record.type == 'Externá chyba'),
          error_records_rek: action.newErrorRecords.filter(error_record => error_record.type == 'Reklamácia'),
        };
      }
      case 'load_documentation': {
        return {
          ...state,
          documentation: action.newDocumentation
        };
      }
      case 'load_self_control': {
        return {
          ...state,
          self_control: action.newSelfControl
        };
      }
      case 'load_measurement_protocol': {
        return {
          ...state,
          measurement_protocol: action.newMeasurementProtocol
        };
      }
      case 'load_ndt_protocol': {
        return {
          ...state,
          ndt_protocol: action.newNDTProtocol
        };
      }
      case 'load_annealing_protocol': {
        return {
          ...state,
          annealing_protocol: action.newAnnealingProtocol
        };
      }
      case 'load_measure_complexities': {
        return {
          ...state,
          measure_complexities: action.newMeasureComplexities
        };
      }
      case 'load_priority_departments': {
        let final_employees = [];
        for(let i = 0;i<action.newPriorityDepartments.length;i++){
          var abbreviation_employees = [];
          
          for(let j = 0;j<action.newPriorityDepartments[i].departments.length;j++){
            abbreviation_employees = [...abbreviation_employees, ...action.newPriorityDepartments[i].departments[j].employees];
          }
          final_employees = [...final_employees,{
            abbreviation: action.newPriorityDepartments[i].abbreviation,
            id: action.newPriorityDepartments[i].id,  
            employees: abbreviation_employees
          }];
        }
        return {
          ...state,
          priority_departments: action.newPriorityDepartments,
          priority_departments_employees: final_employees,
        };
      }
      case 'load_priority_loads': {
        return {
          ...state,
          priority_automatic_loads: action.newPriorityLoads.filter(element => element.priority_type == 'automatic'),
          priority_manually_loads: action.newPriorityLoads.filter(element => element.priority_type == 'manually'),
        };
      }
      case 'priority_departments_reload': {
        return {
          ...state,
          priority_departments_reload: action.value
        };
      }
      case 'load_priorities': {
        return {
          ...state,
          priorities: action.newPriorities
        };
      }
      case 'upsert_priorities': {
        const incoming = Array.isArray(action.priorities) ? action.priorities : [];
        if (!incoming.length) return state;
        const merged = [...state.priorities];
        incoming.forEach((priority) => {
          if (priority?.id == null) return;
          const idx = merged.findIndex((row) => Number(row.id) === Number(priority.id));
          if (idx >= 0) merged[idx] = { ...merged[idx], ...priority };
          else merged.unshift(priority);
        });
        return {
          ...state,
          priorities: merged,
        };
      }
      case 'priorities_reload': {
        return {
          ...state,
          priorities_reload: action.value
        };
      }
      case 'load_control_records': {
        return {
          ...state,
          control_records: action.newControlRecords
        };
      }
      case 'load_users': {
        return {
          ...state,
          users: action.newUsers
        };
      }
      case 'load_crm_traders': {
        return {
          ...state,
          crm_traders: action.newCrmTraders || []
        };
      }
      case 'users_reload': {
        return {
          ...state,
          users_reload: action.value
        };
      }
      case 'load_roles': {
        return {
          ...state,
          roles: action.newRoles
        };
      }
      case 'load_favorites': {
        return {
          ...state,
          favorites: action.newFavorites
        };
      }
      case 'load_fast_view': {
        return {
          ...state,
          fast_view: action.newFastView
        };
      }
      case 'load_user_mask_filters': {
        return {
          ...state,
          user_mask_filters: action.newUserMaskFilters
        };
      }
      case 'load_standard_mask_filters': {
        return {
          ...state,
          standard_mask_filters: action.newStandardMaskFilters
        };
      }
      case 'load_chosen_notifications': {
        return {
          ...state,
          chosen_notifications: action.newChosenNotifications
        };
      }
      case 'login_user': {
        return {
          ...state,
          user: action.newUser
        };
      }
      case 'load_last_used': {
        let last_used = JSON.parse(localStorage.getItem("favorites"));
        if(last_used==undefined)last_used=[];
        last_used = last_used.filter(item => item.user_id === state.user.id);
        if(last_used.length>10)last_used = last_used.slice(0,10);
        return {
          ...state,
          last_used: last_used
        };
      }
      case 'load_painting_intervals':{
        return {
          ...state,
          painting_intervals: action.newPaintingIntervals
        }
      }
      case 'load_painting_conditions':{
        return {
          ...state,
          painting_conditions: action.newPaintingConditions
        }
      }
      case 'load_painting_time_conditions':{
        return {
          ...state,
          painting_time_conditions: action.newPaintingTimeConditions
        }
      }
      case 'load_painting_employee_conditions':{
        return {
          ...state,
          painting_employee_conditions: action.newPaintingEmployeeConditions
        }
      }
      case 'load_weather':{
        return {
          ...state,
          weather: action.newWeather
        }
      }
      case 'load_gauges':{
        return {
          ...state,
          gauges: action.newGauges
        }
      }
      case 'load_gauge_calibrations':{
        return {
          ...state,
          gauge_calibrations: action.newGaugeCalibrations
        }
      }
      case 'load_calibration_companies':{
        return {
          ...state,
          //calibration_companies: action.newCalibrationCompanies
        }
      }
      case 'load_calibration_company_certificates':{
        return {
          ...state,
          //calibration_company_certificates: action.newCalibrationCompanyCertificates
        }
      }
      case 'load_training_companies': {
        return {
          ...state,
          training_companies: action.newTrainingCompanies ?? state.training_companies
        }
      }
      case 'load_training_company_contacts': {
        return {
          ...state,
          training_company_contacts: action.newTrainingCompanyContacts?.filter(contact => contact?.module=='training'),
        }
      }
      case 'load_training_company_certificates':{
        return {
          ...state,
          //training_company_certificates: action.newTrainingCompanyCertificates
        }
      }
      case 'load_training_invitations':{
        return {
          ...state,
          training_invitations: action.newTrainingInvitations
        }
      }
      case 'load_training_invitation_templates':{
        return {
          ...state,
          training_invitation_templates: Array.isArray(action.newTrainingInvitationTemplates)
            ? action.newTrainingInvitationTemplates
            : [],
          training_invitation_templates_loaded: true,
        }
      }
      case 'load_support_tickets':{
        return {
          ...state,
          support_tickets: action.newSupportTickets
        }
      }
      case 'load_support_tickets_open_count':{
        return {
          ...state,
          support_tickets_open_count: action.count ?? 0
        }
      }
      case 'load_service_levels':{
        return {
          ...state,
          service_levels: action.newServiceLevels
        }
      }
      case 'load_service_states':{
        return {
          ...state,
          service_states: action.newServiceStates
        }
      }
      case 'load_service_tasks':{
        return {
          ...state,
          service_tasks: action.newServiceTasks,
          maintenance_service_tasks: action.newServiceTasks?.filter(task => task?.type=="Need" || task?.type=="Error")
        }
      }
      case 'load_service_all_tasks':{
        return {
          ...state,
          service_all_tasks: action.newServiceAllTasks || []
        }
      }
      case 'load_task_templates':{
        return {
          ...state,
          task_templates: action.newTaskTemplates
        }
      }
      case 'serviceTaskUpdatedInDetail': {
        return { ...state, serviceTaskUpdatedInDetail: action.payload };
      }
      case 'support_ticket_updated_in_detail': {
        return { ...state, support_ticket_updated_in_detail: action.payload };
      }
      case 'updateTaskViewed':{
        const taskId = action.payload;
        const value = action?.value ?? false;
        return {
          ...state,
          service_tasks: state.service_tasks.map(task => 
            task.id === taskId ? { ...task, is_unread: value } : task
          ),
          service_all_tasks: (state.service_all_tasks || []).map(task => 
            task.id === taskId ? { ...task, is_unread: value } : task
          ),
          lastTaskViewedUpdate: { taskId, value }
        }
      }
      case 'updateCrmTicketViewed': {
        const ticketId = Number(action.payload);
        const value = action?.value ?? false;
        if (!ticketId) return state;
        return {
          ...state,
          lastCrmTicketViewedUpdate: { ticketId, value, at: Date.now() },
        };
      }
      case 'updateTicketViewed':{
        const ticketId = action.payload;
        const value = action?.value ?? false;
        return {
          ...state,
          support_tickets: (state.support_tickets || []).map(ticket =>
            ticket.id === ticketId ? { ...ticket, is_unread: value } : ticket
          ),
          lastTicketViewedUpdate: { ticketId, value }
        }
      }
      case 'updateRecordViewed':{
        const recordId = action.payload;
        const value = action?.value ?? false;
        return {
          ...state,
          checklist_records: state.checklist_records.map(record => 
            record.id === recordId ? { ...record, is_unread: value } : record
          ),
          checklist_state_records: state.checklist_state_records.map(record => 
            record.id === recordId ? { ...record, is_unread: value } : record
          ),
          checklist_all_records: state.checklist_all_records.map(record => 
            record.id === recordId ? { ...record, is_unread: value } : record
          ),
          lastRecordViewedUpdate: { recordId, value }
        }
      }
      case 'load_service_user_settings':{
        return {
          ...state,
          service_user_settings: action.newServiceUserSettings
        }
      }
      case 'load_crm_levels':{
        return {
          ...state,
          crm_levels: action.newCrmLevels ?? []
        }
      }
      case 'load_crm_level_user_settings':{
        return {
          ...state,
          crm_level_user_settings: action.newCrmLevelUserSettings ?? []
        }
      }
      case 'load_crm_states':{
        return {
          ...state,
          crm_states: action.newCrmStates ?? []
        }
      }
      case 'load_crm_steps':{
        return {
          ...state,
          crm_steps: action.newCrmSteps ?? []
        }
      }
      case 'load_checklist_templates':{
        const allTemplates = Array.isArray(action.newChecklistTemplates) ? action.newChecklistTemplates : [];
        return {
          ...state,
          ...deriveChecklistTemplateCollections(allTemplates, state.user),
          checklist_templates_loaded: true,
        }
      }
      case 'upsert_checklist_template':{
        const next = upsertCanonicalChecklistTemplateRow(
          reconstructChecklistTemplateUniverse(state),
          action.checklistTemplate,
        );
        return {
          ...state,
          ...deriveChecklistTemplateCollections(next, state.user),
        }
      }
      case 'remove_checklist_templates':{
        const next = removeChecklistTemplateRowsByIds(
          reconstructChecklistTemplateUniverse(state),
          action.ids,
        );
        return {
          ...state,
          ...deriveChecklistTemplateCollections(next, state.user),
        }
      }
      case 'load_checklist_records':{
        return {
          ...state,
          checklist_records: action.newChecklistRecords
        }
      }
      case 'load_checklist_state_records':{
        return {
          ...state,
          checklist_state_records: action.newChecklistStateRecords
        }
      }
      case 'load_checklist_all_records':{
        return {
          ...state,
          checklist_all_records: action.newChecklistAllRecords
        }
      }
      case 'load_machines':{
        return {
          ...state,
          machines: action.newMachines
        }
      }
      case 'load_machine_types':{
        return {
          ...state,
          machine_types: action.newMachineTypes
        }
      }
      case 'load_maintenance_types':{
        return {
          ...state,
          maintenance_types: action.newMaintenanceTypes
        }
      }
      case 'create_new_tab': {
        // Additive semantic i18n fields (translationKey/params) — legacy `name`→label unchanged.
        const newTab = {
          value: `${state.tabIndex}`,
          label: action.name,
          legacyLabel: action.legacyLabel != null ? action.legacyLabel : action.name,
          mask: action?.mask || "notfind0001"
        };
        if (action.translationKey) {
          newTab.translationKey = action.translationKey;
          if (action.translationParams && typeof action.translationParams === 'object') {
            newTab.translationParams = action.translationParams;
          }
        }
        const newPanel = {
          value: `${state.tabIndex}`,
          child: action.page,
          props: action.props || null,
        }
        let all_last_used = JSON.parse(localStorage.getItem("favorites"));
        if(all_last_used==undefined)all_last_used=[];
        let my_last_used = all_last_used.filter(item => item.user_id === state.user.id);
        let not_my_last_used = all_last_used.filter(item => item.user_id !== state.user.id);
        my_last_used = my_last_used.filter(item => item.name !== action.name && item.page !== action.page);
        if(my_last_used.length>10)my_last_used = my_last_used.slice(0,10);
        var new_last_used,new_my_last_used;
        if(newPanel?.props?.not_last_used){
          new_last_used = [...my_last_used,...not_my_last_used];
          new_my_last_used = [...my_last_used];
        }
        else{
          new_last_used = [{name:action.name,page:action.page,user_id: state.user.id},...my_last_used,...not_my_last_used];
          new_my_last_used = [{name:action.name,page:action.page,user_id: state.user.id},...my_last_used];
        }
        
        localStorage.setItem("favorites",JSON.stringify(new_last_used));
        return {
          ...state,
          tabs: state.tabs.concat(newTab),
          panels: state.panels.concat(newPanel),
          tabIndex: state.tabIndex+1,
          selectedTab: `${state.tabIndex}`,
          selectedMask: newTab.mask,
          last_used: new_my_last_used
        };
      }
      case 'refresh_calibration_state_list': {
        return { ...state, refreshCalibrationStateList: (state.refreshCalibrationStateList || 0) + 1 };
      }
      case 'refresh_gauge_list': {
        return { ...state, refreshGaugeList: (state.refreshGaugeList || 0) + 1 };
      }
      case 'refresh_gauge_calibration_list': {
        return { ...state, refreshGaugeCalibrationList: (state.refreshGaugeCalibrationList || 0) + 1 };
      }
      case 'refresh_summary_emails_maintenance_list': {
        return { ...state, refreshSummaryEmailsMaintenanceList: (state.refreshSummaryEmailsMaintenanceList || 0) + 1 };
      }
      case 'close_tab': {
        if (action.id == 1 || action.id == 2 || action.id === '1' || action.id === '2') {
          return state;
        }
        //var tabs = state.tabs.filter(x => hiddenTabs.indexOf(x.label) == -1);
        //var panels = state.panels.filter(x => hiddenTabs.indexOf(x.child) == -1);
        var tabs = state.tabs.filter(x => x.value !== action.id);
        var panels = state.panels.filter(x => x.value !== action.id);
        var selectedTab = ""+(panels[panels.length-1].value) + "";
        if(selectedTab=='2')selectedTab='1';
        return {
          ...state,
          tabs: tabs,
          panels: panels,
          selectedTab: selectedTab,
          selectedMask: tabs.filter(x => x.value==selectedTab)[0]?.mask
        };
      }
      case 'change_tab': {
        //const tabs = state.tabs.filter(x => hiddenTabs.indexOf(x.label) == -1);
        //const panels = state.panels.filter(x => hiddenTabs.indexOf(x.child) == -1);
        //console.log(tabs,panels);
        return {
          ...state,
          //tabs: tabs,
          //panels: panels,
          selectedTab: action.name,
          selectedMask: state.tabs.filter(x => x.value==action.name)[0]?.mask || 'notfind001'
        };
      }
      /*case 'add_to_favorites': {
        var found = null;
        function search(menu,title){
            menu.forEach(x => {
              if(x.children){
                search(x.children,title);
              }
              else{
                if(x.page===title){
                  found=x;
                  return;
                }
              }
            })
        }
        const filtered_tab = state.panels.filter(x => x.value === state.selectedTab)[0];
        search(menuItems,filtered_tab.child);
        console.log("SAVE VALUE ",found);
        return {
          ...state,
          favorites: [...new Set(state.favorites.concat(found))]
        };
      }*/

      case 'customers_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          customers_reload: action.value
        };
      }
      case 'suppliers_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          suppliers_reload: action.value
        };
      }
      case 'error_records_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          error_records_reload: action.value
        };
      }
      case 'departments_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          departments_reload: action.value
        };
      }
      case 'employees_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          employees_reload: action.value
        };
      }
      case 'roles_reload': {
        //console.log("SET RELOAD: ",action.value);
        return {
          ...state,
          roles_reload: action.value
        };
      }
      case 'painting_intervals_reload': {
        return {
          ...state,
          painting_intervals_reload: action.value
        }
      }
      case 'painting_conditions_reload': {
        return {
          ...state,
          painting_conditions_reload: action.value
        }
      }
      case 'gauges_reload': {
        return {
          ...state,
          gauges: action.value
        }
      }
      case 'gauge_calibrations_reload': {
        return {
          ...state,
          gauge_calibrations: action.value
        }
      }
      case 'calibration_companies_reload': {
        return {
          ...state,
          calibration_companies: action.value
        }
      }
      case 'calibration_company_certificates_reload': {
        return {
          ...state,
          calibration_company_certificates: action.value
        }
      }
      case 'set_initial_state': {
        resetChecklistTemplatesLookup();
        return initialState;
      }
      case 'load_notifications': {
        return {
          ...state,
          notifications: action.newNotifications,
        };
      }
      case 'add_notification': {
        return {
          ...state,
          notifications: [{id:action.value.id,message: action.value.message,data: action.value.data},...state.notifications],
        };
      }
      case 'clear_notification': {
        return {
          ...state,
          notifications: action.value,
        };
      }
      default:{
        throw Error('Unknown action: ' + action.type);
      }
    }
}
export default DatabaseReducer;